import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { store } from '../store/store';
import { logout, refreshToken } from '../store/slices/authSlice';

// Create Axios instance for main API
const api: AxiosInstance = axios.create({
  baseURL: (import.meta as any).env?.VITE_API_URL || 'http://localhost:8000/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create HMS Auth instance for authentication
const hmsAuthApi: AxiosInstance = axios.create({
  baseURL: (import.meta as any).env?.VITE_HMS_AUTH_URL || 'http://localhost:8002/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const state = store.getState();
    const token = state.auth.token;
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Request interceptor for HMS auth API
hmsAuthApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const state = store.getState();
    const token = state.auth.token;
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Clear auth state on 401
      store.dispatch(logout());
      
      // Redirect to login if not already there
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Response interceptor for HMS auth API
hmsAuthApi.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // For HMS, we don't have refresh tokens, so just logout on 401
      store.dispatch(logout());
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

// Export the api instance as default
export default api;

// API Response types
export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  errors?: string[];
}

// Auth API types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: 'ADMIN' | 'DOCTOR' | 'NURSE' | 'RECEPTIONIST' | 'BILLING_STAFF' | 'LAB_TECHNICIAN' | 'PATIENT';
}

export interface AuthResponse {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
  };
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

// Auth API functions
export const authApi = {
  login: (credentials: LoginRequest): Promise<AxiosResponse<ApiResponse<AuthResponse>>> =>
    hmsAuthApi.post('/auth/hms-login', credentials),
    
  register: (data: RegisterRequest): Promise<AxiosResponse<ApiResponse<AuthResponse>>> =>
    api.post('/auth/register', data),
    
  logout: (): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.post('/auth/logout'),
    
  verify: (): Promise<AxiosResponse<ApiResponse<any>>> =>
    hmsAuthApi.post('/auth/hms-verify'),

  changePassword: (data: ChangePasswordRequest): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.post('/auth/change-password', data),
};

// Patient API types
export interface Patient {
  id: string;
  userId: string;
  patientNumber: string;
  dateOfBirth: string;
  gender: 'MALE' | 'FEMALE' | 'OTHER';
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  emergencyContact: {
    name: string;
    relationship: string;
    phone: string;
    email?: string;
  };
  insuranceInfo?: {
    provider: string;
    policyNumber: string;
    groupNumber?: string;
    expiryDate?: string;
  };
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    isActive: boolean;
    createdAt: string;
    updatedAt?: string;
  };
  _count?: {
    appointments: number;
    medicalHistory: number;
  };
  appointments?: any[];
  medicalHistory?: any[];
  billings?: any[];
}

export interface PatientsListResponse {
  patients: Patient[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export interface PatientStatsResponse {
  totalPatients: number;
  activePatients: number;
  inactivePatients: number;
  newPatientsThisMonth: number;
  genderDistribution: Record<string, number>;
}

export interface CreatePatientRequest {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  dateOfBirth: string;
  gender: 'MALE' | 'FEMALE' | 'OTHER';
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country?: string;
  };
  emergencyContact: {
    name: string;
    relationship: string;
    phone: string;
    email?: string;
  };
  insuranceInfo?: {
    provider: string;
    policyNumber: string;
    groupNumber?: string;
    expiryDate?: string;
  };
}

export interface PatientsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  gender?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Patient API functions
export const patientsApi = {
  getPatients: (params?: PatientsQueryParams): Promise<AxiosResponse<ApiResponse<PatientsListResponse>>> => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }
    const queryString = searchParams.toString();
    return api.get(`/patients${queryString ? `?${queryString}` : ''}`);
  },

  getPatient: (id: string): Promise<AxiosResponse<ApiResponse<Patient>>> =>
    api.get(`/patients/${id}`),

  createPatient: (data: CreatePatientRequest): Promise<AxiosResponse<ApiResponse<Patient>>> =>
    api.post('/patients', data),

  updatePatient: (id: string, data: Partial<CreatePatientRequest>): Promise<AxiosResponse<ApiResponse<Patient>>> =>
    api.put(`/patients/${id}`, data),

  deletePatient: (id: string): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.delete(`/patients/${id}`),

  getPatientMedicalHistory: (id: string): Promise<AxiosResponse<ApiResponse<any[]>>> =>
    api.get(`/patients/${id}/medical-history`),

  addPatientMedicalHistory: (id: string, data: any): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(`/patients/${id}/medical-history`, data),

  getPatientAppointments: (id: string, params?: { page?: number; limit?: number; status?: string; upcoming?: boolean }): Promise<AxiosResponse<ApiResponse<any>>> => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }
    const queryString = searchParams.toString();
    return api.get(`/patients/${id}/appointments${queryString ? `?${queryString}` : ''}`);
  },

  getPatientStats: (): Promise<AxiosResponse<ApiResponse<PatientStatsResponse>>> =>
    api.get('/patients/stats/overview'),
};

// Generic API functions for other endpoints
// Doctor API types
export interface Doctor {
  id: string;
  userId: string;
  licenseNumber: string;
  specialization: string;
  department: string;
  consultationFee: number;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    isActive: boolean;
    createdAt: string;
  };
  _count?: {
    appointments: number;
  };
  availabilitySchedule?: any[];
  tempPassword?: string; // Temporary password for new users
}

export interface DoctorsListResponse {
  doctors: Doctor[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export interface CreateDoctorRequest {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  licenseNumber: string;
  specialization: string;
  department: string;
  consultationFee: number;
}

export interface UpdateDoctorRequest {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  licenseNumber: string;
  specialization: string;
  department: string;
  consultationFee: number;
}

export interface DoctorsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  specialization?: string;
  department?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface DoctorStatsResponse {
  totalDoctors: number;
  activeDoctors: number;
  inactiveDoctors: number;
  specializationDistribution: Record<string, number>;
  departmentDistribution: Record<string, number>;
}

// Schedule Management Types
export interface ScheduleEntry {
  id?: string;
  doctorId?: string;
  customerId?: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface DoctorScheduleResponse {
  schedule: ScheduleEntry[];
}

// User Management Types
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  role: 'ADMIN' | 'DOCTOR' | 'NURSE' | 'RECEPTIONIST' | 'BILLING_STAFF' | 'LAB_TECHNICIAN' | 'PATIENT';
  isActive: boolean;
  lastLoginAt?: string | null;
  createdAt: string;
  updatedAt: string;
  doctor?: {
    id: string;
    licenseNumber: string;
    specialization: string;
    department: string;
  };
  patient?: {
    id: string;
    patientNumber: string;
  };
}

export interface UsersListResponse {
  users: User[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export interface UsersQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface ResetPasswordRequest {
  newPassword: string;
  confirmPassword: string;
}

export interface UpdateUserStatusRequest {
  isActive: boolean;
}

export interface UserStatsResponse {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  roleDistribution: Record<string, number>;
}

// User API functions
export const usersApi = {
  getUsers: (params?: UsersQueryParams): Promise<AxiosResponse<ApiResponse<UsersListResponse>>> =>
    api.get('/users', { params }),
    
  getUser: (id: string): Promise<AxiosResponse<ApiResponse<User>>> =>
    api.get(`/users/${id}`),

  resetPassword: (id: string, data: ResetPasswordRequest): Promise<AxiosResponse<ApiResponse<{ tempPassword: string; email: string }>>> =>
    api.put(`/users/${id}/password`, data),

  updateStatus: (id: string, data: UpdateUserStatusRequest): Promise<AxiosResponse<ApiResponse<User>>> =>
    api.put(`/users/${id}/status`, data),

  // Alias for compatibility
  toggleUserStatus: (id: string, data: UpdateUserStatusRequest): Promise<AxiosResponse<ApiResponse<User>>> =>
    api.put(`/users/${id}/status`, data),

  getStats: (): Promise<AxiosResponse<ApiResponse<UserStatsResponse>>> =>
    api.get('/users/stats/overview'),
};

// Appointment API types
export interface Appointment {
  id: string;
  patientId: string;
  doctorId: string;
  appointmentDate: string;
  appointmentTime: string;
  status: 'SCHEDULED' | 'CONFIRMED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  patient: {
    id: string;
    patientNumber: string;
    user: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string | null;
    };
  };
  doctor: {
    id: string;
    licenseNumber: string;
    specialization: string;
    department: string;
    user: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
  };
}

export interface AppointmentsListResponse {
  appointments: Appointment[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export interface CreateAppointmentRequest {
  patientId: string;
  doctorId: string;
  appointmentDate: string;
  appointmentTime: string;
  notes?: string;
}

export interface UpdateAppointmentRequest {
  patientId?: string;
  doctorId?: string;
  appointmentDate?: string;
  appointmentTime?: string;
  notes?: string;
  status?: 'SCHEDULED' | 'CONFIRMED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
}

export type AppointmentStatus = 'SCHEDULED' | 'CONFIRMED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';

export interface AppointmentsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  doctorId?: string;
  patientId?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface AppointmentStatsResponse {
  totalAppointments: number;
  todayAppointments: number;
  scheduledAppointments: number;
  completedAppointments: number;
  cancelledAppointments: number;
  upcomingAppointments: number;
}

export interface DoctorAvailabilityResponse {
  date: string;
  bookedTimes: string[];
  availableTimes: string[];
  schedule: {
    id: string;
    doctorId: string;
    dayOfWeek: 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';
    startTime: string;
    endTime: string;
    isAvailable: boolean;
  } | null;
  message?: string;  // Optional message field for when no schedule is available
}

// Appointment API functions
export const appointmentsApi = {
  getAppointments: (params?: AppointmentsQueryParams): Promise<AxiosResponse<ApiResponse<AppointmentsListResponse>>> => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }
    // Add include parameter for nested relations
    searchParams.append('include', 'patient.user,doctor.user');
    
    const queryString = searchParams.toString();
    return api.get(`/appointments${queryString ? `?${queryString}` : ''}`);
  },

  getAppointment: (id: string): Promise<AxiosResponse<ApiResponse<Appointment>>> =>
    api.get(`/appointments/${id}`, {
      params: {
        include: 'patient.user,doctor.user'
      }
    }),

  createAppointment: (data: CreateAppointmentRequest): Promise<AxiosResponse<ApiResponse<Appointment>>> =>
    api.post('/appointments', data),

  updateAppointment: (id: string, data: UpdateAppointmentRequest): Promise<AxiosResponse<ApiResponse<Appointment>>> =>
    api.put(`/appointments/${id}`, data),

  cancelAppointment: (id: string): Promise<AxiosResponse<ApiResponse<Appointment>>> =>
    api.delete(`/appointments/${id}`),

  getAppointmentStats: (): Promise<AxiosResponse<ApiResponse<AppointmentStatsResponse>>> =>
    api.get('/appointments/stats/overview'),

  getDoctorAvailability: (doctorId: string, date: string): Promise<AxiosResponse<ApiResponse<DoctorAvailabilityResponse>>> => {
    // Ensure we're using the local date without time component
    const localDate = new Date(date);
    localDate.setHours(0, 0, 0, 0);
    const formattedDate = localDate.toISOString().split('T')[0];
    
    return api.get(`/appointments/doctor/${doctorId}/availability`, {
      params: { date: formattedDate }
    });
  },
};

// Dashboard API types
export interface DashboardStats {
  summary: {
    totalPatients: number;
    totalDoctors: number;
    todayAppointments: number;
    upcomingAppointments: number;
  };
  patients: {
    total: number;
    active: number;
    newThisMonth: number;
    growth: number;
  };
  doctors: {
    total: number;
    active: number;
  };
  appointments: {
    total: number;
    today: number;
    thisWeek: number;
    thisMonth: number;
    scheduled: number;
    completed: number;
    cancelled: number;
    upcoming: number;
    todayBreakdown: Record<string, number>;
  };
  recentActivity: Array<{
    id: string;
    patientName: string;
    doctorName: string;
    date: string;
    time: string;
    status: string;
    createdAt: string;
  }>;
  monthlyTrends: Array<{
    month: string;
    appointments: number;
  }>;
  insights: {
    busyDay: string;
    patientTrend: string;
    utilizationRate: number;
  };
}

export interface DashboardAlert {
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high';
  actionUrl: string;
}

export interface DashboardAlertsResponse {
  alerts: DashboardAlert[];
  count: number;
}

// Dashboard API functions
export const dashboardApi = {
  getStats: (): Promise<AxiosResponse<ApiResponse<DashboardStats>>> =>
    api.get('/dashboard/stats/overview'),

  getAlerts: (): Promise<AxiosResponse<ApiResponse<DashboardAlertsResponse>>> =>
    api.get('/dashboard/alerts'),
};

// Billing API
export const billingAPI = {
  // Get all billing records with search and filtering
  getBillings: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    patientId?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get(`/billing?${searchParams}`);
    return response.data;
  },

  // Get billing statistics
  getBillingStats: async () => {
    const response = await api.get('/billing/stats/overview');
    return response.data;
  },

  // Get single billing record
  getBilling: async (id: string) => {
    const response = await api.get(`/billing/${id}`);
    return response.data;
  },

  // Create new billing record
  createBilling: async (data: {
    patientId: string;
    appointmentId?: string;
    items: Array<{
      description: string;
      quantity: number;
      unitPrice: number;
    }>;
    tax?: number;
    discount?: number;
    dueDate: string;
    notes?: string;
  }) => {
    const response = await api.post('/billing', data);
    return response.data;
  },

  // Create billing from appointment
  createBillingFromAppointment: async (appointmentId: string, data?: {
    additionalItems?: Array<{
      description: string;
      quantity: number;
      unitPrice: number;
    }>;
    tax?: number;
    discount?: number;
    dueDate?: string;
  }) => {
    const response = await api.post(`/billing/appointment/${appointmentId}`, data);
    return response.data;
  },

  // Process payment
  processPayment: async (id: string, data: {
    amount: number;
    paymentMethod: string;
    transactionId?: string;
    notes?: string;
  }) => {
    const response = await api.put(`/billing/${id}/payment`, data);
    return response.data;
  },

  // Update billing record
  updateBilling: async (id: string, data: {
    items?: Array<{
      description: string;
      quantity: number;
      unitPrice: number;
    }>;
    tax?: number;
    discount?: number;
    dueDate?: string;
    paymentStatus?: string;
    paymentMethod?: string;
    transactionId?: string;
  }) => {
    const response = await api.put(`/billing/${id}`, data);
    return response.data;
  },

  // Cancel billing record
  cancelBilling: async (id: string) => {
    const response = await api.delete(`/billing/${id}`);
    return response.data;
  }
};

export const apiClient = {
  get: <T>(url: string): Promise<AxiosResponse<ApiResponse<T>>> => api.get(url),
  post: <T>(url: string, data?: any): Promise<AxiosResponse<ApiResponse<T>>> => api.post(url, data),
  put: <T>(url: string, data?: any): Promise<AxiosResponse<ApiResponse<T>>> => api.put(url, data),
  patch: <T>(url: string, data?: any): Promise<AxiosResponse<ApiResponse<T>>> => api.patch(url, data),
  delete: <T>(url: string): Promise<AxiosResponse<ApiResponse<T>>> => api.delete(url),
};

// Settings API types
export interface Settings {
  id: string;
  hospitalName: string;
  currency: 'USD' | 'GBP' | 'EUR' | 'PKR' | 'INR' | 'CAD' | 'AUD' | 'SAR' | 'AED';
  hospitalAddress?: string;
  hospitalPhone?: string;
  hospitalEmail?: string;
  hospitalWebsite?: string;
  hospitalLogo?: string;
  timeZone: string;
  dateFormat: string;
  timeFormat: string;
  createdAt: string;
  updatedAt: string;
}

export interface CurrencyInfo {
  code: string;
  name: string;
  symbol: string;
}

export interface TimezoneInfo {
  value: string;
  label: string;
}

export interface UpdateSettingsRequest {
  hospitalName: string;
  currency: 'USD' | 'GBP' | 'EUR' | 'PKR' | 'INR' | 'CAD' | 'AUD' | 'SAR' | 'AED';
  hospitalAddress?: string;
  hospitalPhone?: string;
  hospitalEmail?: string;
  hospitalWebsite?: string;
  hospitalLogo?: string;
  timeZone?: string;
  dateFormat?: string;
  timeFormat?: string;
}

export interface UserPreferences {
  id: string;
  userId: string;
  dateFormat: string;
  timeFormat: string;
  timeZone: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateUserPreferencesRequest {
  dateFormat?: string;
  timeFormat?: string;
  timeZone?: string;
}

// Settings API functions
export const settingsApi = {
  getPublicSettings: (): Promise<AxiosResponse<ApiResponse<Partial<Settings>>>> =>
    api.get('/settings/public'),
    
  getSettings: (): Promise<AxiosResponse<ApiResponse<Settings>>> =>
    api.get('/settings'),
    
  updateSettings: (data: UpdateSettingsRequest): Promise<AxiosResponse<ApiResponse<Settings>>> =>
    api.put('/settings', data),
    
  getCurrencies: (): Promise<AxiosResponse<ApiResponse<CurrencyInfo[]>>> =>
    api.get('/settings/currencies'),
    
  getTimezones: (): Promise<AxiosResponse<ApiResponse<TimezoneInfo[]>>> =>
    api.get('/settings/timezones'),

  getUserPreferences: (): Promise<AxiosResponse<ApiResponse<UserPreferences>>> =>
    api.get('/settings/preferences'),

  updateUserPreferences: (data: UpdateUserPreferencesRequest): Promise<AxiosResponse<ApiResponse<UserPreferences>>> =>
    api.put('/settings/preferences', data),
};

// Notification API types
export interface Notification {
  id: string;
  userId: string;
  title: string;
  message: string;
  type: 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR' | 'APPOINTMENT_REMINDER' | 'PAYMENT_DUE';
  isRead: boolean;
  data?: Record<string, any>;
  createdAt: string;
}

export interface NotificationsListResponse {
  notifications: Notification[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    unreadCount: number;
  };
}

export interface NotificationsQueryParams {
  page?: number;
  limit?: number;
  unreadOnly?: boolean;
}

export interface CreateNotificationRequest {
  userId: string;
  title: string;
  message: string;
  type: 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR' | 'APPOINTMENT_REMINDER' | 'PAYMENT_DUE';
  data?: Record<string, any>;
}

// Notifications API functions
export const notificationsApi = {
  getNotifications: (params?: NotificationsQueryParams): Promise<AxiosResponse<ApiResponse<NotificationsListResponse>>> => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }
    const queryString = searchParams.toString();
    return api.get(`/notifications${queryString ? `?${queryString}` : ''}`);
  },

  getUnreadCount: (): Promise<AxiosResponse<ApiResponse<{ unreadCount: number }>>> =>
    api.get('/notifications/unread-count'),

  markAsRead: (id: string): Promise<AxiosResponse<ApiResponse<Notification>>> =>
    api.patch(`/notifications/${id}/read`),

  markAllAsRead: (): Promise<AxiosResponse<ApiResponse<{ message: string }>>> =>
    api.patch('/notifications/mark-all-read'),

  createNotification: (data: CreateNotificationRequest): Promise<AxiosResponse<ApiResponse<Notification>>> =>
    api.post('/notifications', data),
};

// Password change types
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// Doctors API
export const doctorsApi = {
  getDoctors: (params?: DoctorsQueryParams): Promise<AxiosResponse<ApiResponse<DoctorsListResponse>>> =>
    api.get('/doctors', { params }),
    
  getDoctor: (id: string): Promise<AxiosResponse<ApiResponse<Doctor>>> =>
    api.get(`/doctors/${id}`),
    
  createDoctor: (data: CreateDoctorRequest): Promise<AxiosResponse<ApiResponse<Doctor>>> =>
    api.post('/doctors', data),
    
  updateDoctor: (id: string, data: UpdateDoctorRequest): Promise<AxiosResponse<ApiResponse<Doctor>>> =>
    api.put(`/doctors/${id}`, data),

  getStats: (): Promise<AxiosResponse<ApiResponse<DoctorStatsResponse>>> =>
    api.get('/doctors/stats/overview'),

  // Schedule management
  getSchedule: (doctorId: string): Promise<AxiosResponse<ApiResponse<DoctorScheduleResponse>>> =>
    api.get(`/doctors/${doctorId}/schedule`),

  updateSchedule: (doctorId: string, schedule: ScheduleEntry[]): Promise<AxiosResponse<ApiResponse<DoctorScheduleResponse>>> =>
    api.put(`/doctors/${doctorId}/schedule`, { schedules: schedule }),

  deleteScheduleEntry: (doctorId: string, scheduleId: string): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.delete(`/doctors/${doctorId}/schedule/${scheduleId}`)
};

// Schedule API functions
export const scheduleApi = {
  getSchedule: (doctorId: string): Promise<AxiosResponse<ApiResponse<ScheduleEntry[]>>> =>
    api.get(`/doctors/${doctorId}/schedule`),
    
  updateSchedule: (doctorId: string, schedule: ScheduleEntry[]): Promise<AxiosResponse<ApiResponse<ScheduleEntry[]>>> =>
    api.put(`/doctors/${doctorId}/schedule`, { schedules: schedule }),
    
  getAvailability: (doctorId: string, date: string): Promise<AxiosResponse<ApiResponse<DoctorAvailabilityResponse>>> =>
    api.get(`/doctors/${doctorId}/availability`, { params: { date } }),
};